import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
} from "@mui/material";
import {
  History,
  Visibility,
  PictureAsPdf,
  Close,
  CheckCircle,
  Cancel,
  Circle,
} from "@mui/icons-material";
import theme from "../../theme/theme";
import TableComponent from "../Table";
import {
  fetchServiceCalendar,
  openChecklistPdf,
} from "../../api/ServiceService";
import { toast } from "react-toastify";

const STATUS_CONFIG = {
  completed: { bg: "#E8F5E9", color: "#2E7D32", label: "Completed" },
  pending: { bg: "#E3F2FD", color: "#1565C0", label: "Pending" },
  in_progress: { bg: "#FFF8E1", color: "#F57F17", label: "In Progress" },
  cancelled: { bg: "#FFEBEE", color: "#C62828", label: "Cancelled" },
};

const CHECKLIST_SECTIONS = [
  {
    title: "Cleaning",
    color: "#1565C0",
    fields: [
      { key: "cleaning_machine_room", label: "Machine Room" },
      { key: "cleaning_controller", label: "Controller" },
      { key: "cleaning_machine_unit", label: "Machine Unit" },
      { key: "cleaning_cabin", label: "Cabin" },
      { key: "cleaning_car_doors", label: "Car Doors" },
      { key: "cleaning_car_sill", label: "Car Sill" },
      { key: "cleaning_landing_doors", label: "Landing Doors" },
      { key: "cleaning_landing_sills", label: "Landing Sills" },
      { key: "cleaning_pit", label: "Pit" },
    ],
  },
  {
    title: "Checking",
    color: "#F57F17",
    fields: [
      { key: "checking_guide_rail", label: "Guide Rail" },
      { key: "checking_over_speed_governer", label: "Over Speed Governor" },
      { key: "checking_locks", label: "Locks" },
      { key: "checking_flor_reeds", label: "Floor Reeds" },
      { key: "checking_car_shoe", label: "Car Shoe" },
      { key: "checking_pit_pulley", label: "Pit Pulley" },
      { key: "checking_tracks", label: "Tracks" },
      { key: "checking_limit_switches", label: "Limit Switches" },
      { key: "checking_counter_shoe", label: "Counter Shoe" },
      { key: "checking_pedestal", label: "Pedestal" },
      { key: "checking_ropes", label: "Ropes" },
      { key: "checking_ard", label: "ARD" },
    ],
  },
  {
    title: "Lubrication",
    color: "#2E7D32",
    fields: [
      { key: "lubrication_car_guide_rail", label: "Car Guide Rail" },
      { key: "lubrication_counter_guide_rail", label: "Counter Guide Rail" },
      { key: "lubrication_gear_box_oil_level", label: "Gear Box Oil Level" },
      { key: "lubrication_pit_pulley", label: "Pit Pulley" },
      { key: "lubrication_car_shoe", label: "Car Shoe" },
      { key: "lubrication_door_tracks_gates", label: "Door Tracks & Gates" },
      { key: "lubrication_over_speed_governer", label: "Over Speed Governor" },
      { key: "lubrication_counter_shoe", label: "Counter Shoe" },
      { key: "lubrication_retaining_cam", label: "Retaining Cam" },
    ],
  },
];

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatEnum = (val) => {
  if (!val) return "-";
  return val
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const ServiceHistorySection = ({ projectId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewEntry, setViewEntry] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await fetchServiceCalendar({
        project_id: projectId,
        per_page: 100,
      });
      const data = res.data?.data || res.data || [];
      setEntries(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load service history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) loadHistory();
  }, [projectId]);

  const handleViewPdf = async (entryId) => {
    setPdfLoading(entryId);
    try {
      await openChecklistPdf(entryId);
    } catch (err) {
      toast.error(err?.message || "Failed to open checklist PDF. Checklist may not exist.");
    } finally {
      setPdfLoading(null);
    }
  };

  const checklist = viewEntry?.checklist;

  return (
    <>
      <Box sx={{ mt: 2.5 }}>
        <Paper sx={{ p: 3, borderRadius: theme.paper.borderRadius }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <History
                fontSize="small"
                sx={{ color: theme.palette.primary.main }}
              />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary"
              >
                Service History
              </Typography>
              {entries.length > 0 && (
                <Chip
                  label={entries.length}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    backgroundColor: theme.palette.primary.main + "18",
                    color: theme.palette.primary.main,
                    ml: 0.5,
                  }}
                />
              )}
            </Box>
          </Box>

          {loading ? (
            <Box>
              <Skeleton variant="rounded" height={50} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={200} />
            </Box>
          ) : entries.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <History
                sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                No service history available
              </Typography>
            </Box>
          ) : (
            <TableComponent
              columns={[
                {
                  field: "service_date",
                  headerName: "Date",
                  render: (r) => formatDate(r.service_date),
                },
                {
                  field: "service_type",
                  headerName: "Service Type",
                  render: (r) => formatEnum(r.service?.service_type),
                },
                {
                  field: "service_status",
                  headerName: "Status",
                  render: (r) => {
                    const cfg = STATUS_CONFIG[r.service_status] || STATUS_CONFIG.pending;
                    return (
                      <Chip
                        label={cfg.label}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: 11,
                          backgroundColor: cfg.bg,
                          color: cfg.color,
                        }}
                      />
                    );
                  },
                },
                {
                  field: "service_employee",
                  headerName: "Assigned To",
                  render: (r) => r.service_employee?.name || "-",
                },
                {
                  field: "actions",
                  headerName: "Actions",
                  render: (r) => (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => setViewEntry(r)}
                          sx={{ color: theme.palette.primary.main }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {r.service_status === "completed" && (
                        <Tooltip title="View Checklist PDF">
                          <IconButton
                            size="small"
                            onClick={() => handleViewPdf(r.id)}
                            disabled={pdfLoading === r.id}
                            sx={{ color: "#C62828" }}
                          >
                            <PictureAsPdf fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ),
                },
              ]}
              data={entries}
              pagination={false}
            />
          )}
        </Paper>
      </Box>

      {/* View Details Dialog */}
      <Dialog
        open={!!viewEntry}
        onClose={() => setViewEntry(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: theme.dialog.borderRadius },
        }}
      >
        {viewEntry && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: theme.dialog.headerBgColor,
                py: 2,
              }}
            >
              <Typography variant="h6" fontWeight="700">
                Service Entry Details
              </Typography>
              <IconButton onClick={() => setViewEntry(null)} size="small">
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              {/* Basic Info */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <InfoItem label="Service Date" value={formatDate(viewEntry.service_date)} />
                <InfoItem
                  label="Status"
                  value={
                    <Chip
                      label={(STATUS_CONFIG[viewEntry.service_status] || STATUS_CONFIG.pending).label}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: 11,
                        backgroundColor: (STATUS_CONFIG[viewEntry.service_status] || STATUS_CONFIG.pending).bg,
                        color: (STATUS_CONFIG[viewEntry.service_status] || STATUS_CONFIG.pending).color,
                      }}
                    />
                  }
                />
                <InfoItem label="Service Type" value={formatEnum(viewEntry.service?.service_type)} />
                <InfoItem label="Assigned To" value={viewEntry.service_employee?.name || "-"} />
                <InfoItem label="Started At" value={viewEntry.started_at ? new Date(viewEntry.started_at).toLocaleString() : "-"} />
                <InfoItem label="Completed At" value={viewEntry.completed_at ? new Date(viewEntry.completed_at).toLocaleString() : "-"} />
              </Box>

              {/* Checklist Section */}
              {checklist && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 1.5 }}>
                    Service Checklist
                  </Typography>
                  {CHECKLIST_SECTIONS.map((section) => (
                    <Box key={section.title} sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        fontWeight="700"
                        sx={{
                          color: section.color,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                        }}
                      >
                        {section.fields.map((f) => (
                          <Chip
                            key={f.key}
                            icon={
                              checklist[f.key] ? (
                                <CheckCircle sx={{ fontSize: 16 }} />
                              ) : (
                                <Cancel sx={{ fontSize: 16 }} />
                              )
                            }
                            label={f.label}
                            size="small"
                            sx={{
                              fontSize: 11,
                              fontWeight: 500,
                              backgroundColor: checklist[f.key]
                                ? "#E8F5E9"
                                : "#FFEBEE",
                              color: checklist[f.key] ? "#2E7D32" : "#C62828",
                              "& .MuiChip-icon": {
                                color: checklist[f.key]
                                  ? "#2E7D32"
                                  : "#C62828",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                  {checklist.remarks && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="600">
                        Remarks
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {checklist.remarks}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* PDF button for completed entries */}
              {viewEntry.service_status === "completed" && (
                <Box sx={{ mt: 2.5, display: "flex", justifyContent: "center" }}>
                  <Chip
                    icon={<PictureAsPdf sx={{ fontSize: 16 }} />}
                    label="View Checklist PDF"
                    clickable
                    onClick={() => handleViewPdf(viewEntry.id)}
                    sx={{
                      fontWeight: 600,
                      color: "#C62828",
                      borderColor: "#C62828",
                      "&:hover": { backgroundColor: "#FFEBEE" },
                    }}
                    variant="outlined"
                  />
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

const InfoItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    {typeof value === "string" || typeof value === "number" ? (
      <Typography variant="body2" fontWeight="600">
        {value || "-"}
      </Typography>
    ) : (
      value
    )}
  </Box>
);

export default ServiceHistorySection;
